import { useEffect, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { loadDataSnapshot } from "./lib/api";
import { queryKeys } from "./lib/query-client";
import { useRoute } from "./router";
import { Monitor } from "./routes/Monitor";
import { Dashboard } from "./routes/Dashboard";
import { History } from "./routes/History";
import { Guide } from "./routes/Guide";
import { Graph } from "./routes/Graph";
import { Insights } from "./routes/Insights";
import { Workbench } from "./routes/Workbench";
import { Runs } from "./routes/Runs";
import { SkillDetail } from "./routes/SkillDetail";
import { SkipLink } from "./components/SkipLink";
import { CommandPalette } from "./components/CommandPalette";
import { PipelineBanner } from "./components/PipelineBanner";

const ROUTE_TITLES: Record<string, string> = {
  monitor:   "Monitor",
  history:   "History",
  dashboard: "Overview",
  guide:     "Guide",
  graph:     "Knowledge graph",
  insights:  "Insights",
  workbench: "Workbench",
  runs:      "Runs",
};

export function App() {
  const [route] = useRoute();
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    if (route.name === "skill") {
      document.title = `${route.skillName} · skill-grinder`;
      return;
    }
    document.title = `${ROUTE_TITLES[route.name] ?? "monitor"} · skill-grinder`;
  }, [route.name]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k" && (e.metaKey || e.shiftKey)) {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
  const { data, isLoading, error, isFetching, dataUpdatedAt } = useQuery({
    queryKey: queryKeys.snapshot,
    queryFn: loadDataSnapshot,
  });

  if (isLoading && !data) {
    return (
      <div className="flex h-screen items-center justify-center font-mono text-t-12 uppercase tracking-widest text-muted">
        <span>// loading skills</span>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="flex h-screen items-center justify-center px-12 text-center">
        <div>
          <h1 className="font-display text-t-40 font-extrabold leading-none">
            Could not load data.
          </h1>
          <p className="mt-4 font-mono text-t-12 uppercase tracking-widest text-muted">
            // run <code className="text-accent">npm run build:data</code> first
          </p>
        </div>
      </div>
    );
  }

  let page: ReactNode;
  switch (route.name) {
    case "monitor":   page = <Monitor data={data} />; break;
    case "history":   page = <History data={data} />; break;
    case "dashboard": page = <Dashboard data={data} />; break;
    case "guide":     page = <Guide data={data} />; break;
    case "graph":     page = <Graph data={data} />; break;
    case "insights":  page = <Insights data={data} />; break;
    case "workbench": page = <Workbench data={data} />; break;
    case "runs":      page = <Runs data={data} />; break;
    case "skill":     page = <SkillDetail data={data} skillName={route.skillName} />; break;
  }

  return (
    <>
      <SkipLink />
      <CommandPalette data={data} open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <PipelineBanner />
      {isFetching && data && (
        <div
          className="fixed top-0 left-0 right-0 z-50 bg-accent text-bg font-mono text-[10px] uppercase tracking-[0.14em] text-center py-1"
          role="status"
          aria-live="polite"
        >
          // refreshing data…
        </div>
      )}
      <div id="main-content" tabIndex={-1} className="outline-none" data-updated-at={dataUpdatedAt}>
        {page}
      </div>
    </>
  );
}
